import type { OrgUser, Tag, UserSummary } from '@pm/schemas';
import {
  Avatar,
  Button,
  Checkbox,
  cn,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  Tag as TagChip,
} from '@pm/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, Plus, Search, UserRound, X } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { keys, useContexts, useTags, useUsers } from '@/lib/queries';
import { tagColorFor } from '@/lib/tag-palette';

/** Seletor de usuário (responsável) com busca. `nullable` permite "Não atribuída". */
export function UserPicker({
  value,
  onChange,
  nullable = true,
  placeholder = 'Não atribuída',
  size = 'md',
  disabled,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  nullable?: boolean;
  placeholder?: string;
  size?: 'sm' | 'md';
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const { data } = useUsers({ status: 'ACTIVE' });
  const users = data?.data ?? [];
  const selected = users.find((u) => u.id === value);
  const filtered = useMemo(
    () =>
      users.filter(
        (u) => u.name.toLowerCase().includes(q.toLowerCase()) || u.email.includes(q.toLowerCase()),
      ),
    [users, q],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex w-full items-center gap-2 rounded border border-border-strong bg-surface-1 px-2.5 text-left text-base hover:border-accent-border focus-visible:outline-none focus-visible:shadow-ring disabled:opacity-60',
            size === 'sm' ? 'h-control-sm text-sm' : 'h-control',
          )}
        >
          {selected ? (
            <>
              <Avatar name={selected.name} size="xs" />
              <span className="truncate">{selected.name}</span>
            </>
          ) : (
            <>
              <span className="grid size-[18px] place-items-center rounded-full border border-dashed border-warning text-warning">
                <UserRound className="size-2.5" />
              </span>
              <span className={cn('truncate', value ? '' : 'text-warning')}>{placeholder}</span>
            </>
          )}
          <ChevronDown className="ml-auto size-3.5 text-text-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-1.5">
        <Input
          leading={<Search />}
          placeholder="Buscar pessoa"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
          className="h-control-sm text-sm"
        />
        <ul className="mt-1.5 max-h-[240px] overflow-y-auto">
          {nullable ? (
            <li>
              <PickerRow active={value === null} onClick={() => (onChange(null), setOpen(false))}>
                <span className="text-text-2">Não atribuída</span>
              </PickerRow>
            </li>
          ) : null}
          {filtered.map((u) => (
            <li key={u.id}>
              <PickerRow active={u.id === value} onClick={() => (onChange(u.id), setOpen(false))}>
                <Avatar name={u.name} size="xs" />
                <span className="min-w-0 flex-1 truncate">{u.name}</span>
                <span className="truncate font-mono text-[11px] text-text-3">{u.email}</span>
              </PickerRow>
            </li>
          ))}
          {filtered.length === 0 ? (
            <li className="px-2 py-3 text-sm text-text-3">Ninguém encontrado.</li>
          ) : null}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function PickerRow({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-8 w-full items-center gap-2 rounded px-2 text-left text-base hover:bg-surface-2',
        active && 'bg-accent-soft',
      )}
    >
      {children}
      {active ? <Check className="size-3.5 text-accent" /> : null}
    </button>
  );
}

/** Multi-seleção de usuários (participantes, membros). */
export function UsersMultiPicker({
  value,
  onChange,
  exclude = [],
  label = 'Adicionar',
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  exclude?: (string | null | undefined)[];
  label?: string;
}) {
  const [q, setQ] = useState('');
  const { data } = useUsers({ status: 'ACTIVE' });
  const users = (data?.data ?? []).filter((u) => !exclude.includes(u.id));
  const filtered = users.filter((u) => u.name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm">
          <UserRound /> {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-1.5">
        <Input
          leading={<Search />}
          placeholder="Buscar pessoa"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
          className="h-control-sm text-sm"
        />
        <ul className="mt-1.5 max-h-[240px] overflow-y-auto">
          {filtered.map((u) => (
            <li key={u.id}>
              <label className="flex h-8 cursor-pointer items-center gap-2 rounded px-2 hover:bg-surface-2">
                <Checkbox checked={value.includes(u.id)} onCheckedChange={() => toggle(u.id)} />
                <Avatar name={u.name} size="xs" />
                <span className="truncate">{u.name}</span>
              </label>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

export function UserChips({
  users,
  onRemove,
}: {
  users: (UserSummary | OrgUser)[];
  onRemove?: (id: string) => void;
}) {
  if (users.length === 0) return <span className="text-sm text-text-3">Ninguém</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {users.map((u) => (
        <span
          key={u.id}
          className="inline-flex h-6 items-center gap-1.5 rounded-sm border border-border bg-surface-2 pl-1 pr-1.5 text-sm"
        >
          <Avatar name={u.name} size="xs" />
          {u.name}
          {onRemove ? (
            <button
              type="button"
              aria-label={`Remover ${u.name}`}
              onClick={() => onRemove(u.id)}
              className="text-text-3 hover:text-danger"
            >
              <X className="size-3" />
            </button>
          ) : null}
        </span>
      ))}
    </div>
  );
}

/** Seleção de tags com criação rápida. */
/**
 * Tags da demanda: chips com "x" para remover, busca na lista e, para quem gerencia tags,
 * criação na hora ("Criar tag …"). `readOnly` só mostra as tags.
 */
export function TagPicker({
  value,
  onChange,
  canManage,
  readOnly,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  canManage?: boolean;
  readOnly?: boolean;
}) {
  const { data: tags = [] } = useTags();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: (name: string) => api.post<Tag>('/tags', { name, color: tagColorFor(name) }),
    onSuccess: (tag) => {
      void qc.invalidateQueries({ queryKey: keys.tags });
      onChange([...value, tag.id]);
      setQuery('');
      setError(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Não foi possível criar.'),
  });

  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  const selected = tags.filter((t) => value.includes(t.id));
  const q = query.trim();
  const fold = (v: string) => v.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  const shown = q ? tags.filter((t) => fold(t.name).includes(fold(q))) : tags;
  const exact = q ? tags.find((t) => fold(t.name) === fold(q)) : undefined;
  const canCreate = Boolean(canManage && q && !exact && q.length <= 40);

  const onEnter = () => {
    if (exact) toggle(exact.id);
    else if (canCreate && !create.isPending) create.mutate(q);
    else if (shown.length === 1) toggle(shown[0]!.id);
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {selected.map((t) => (
        <TagChip key={t.id} color={t.color ?? undefined} className="gap-0.5 pr-0.5">
          {t.name}
          {readOnly ? null : (
            <button
              type="button"
              aria-label={`Remover tag ${t.name}`}
              onClick={() => toggle(t.id)}
              className="inline-flex size-4 items-center justify-center rounded-sm opacity-70 hover:bg-surface-3 hover:opacity-100"
            >
              <X className="size-3" />
            </button>
          )}
        </TagChip>
      ))}
      {readOnly ? (
        selected.length === 0 ? (
          <span className="text-sm text-text-3">—</span>
        ) : null
      ) : (
        <Popover
          open={open}
          onOpenChange={(o) => {
            setOpen(o);
            if (!o) {
              setQuery('');
              setError(null);
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 px-1.5 text-sm">
              <Plus /> Tag
            </Button>
          </PopoverTrigger>
          <PopoverContent className="flex w-[260px] flex-col gap-1.5 p-1.5">
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onEnter();
                }
              }}
              placeholder={canManage ? 'Buscar ou criar tag' : 'Buscar tag'}
              leading={<Search />}
              maxLength={40}
              className="h-control-sm text-sm"
            />
            {shown.length > 0 ? (
              <ul className="scroll-subtle max-h-[220px]">
                {shown.map((t: Tag) => (
                  <li key={t.id}>
                    <label className="flex h-8 cursor-pointer items-center gap-2 rounded px-2 hover:bg-surface-2">
                      <Checkbox
                        checked={value.includes(t.id)}
                        onCheckedChange={() => toggle(t.id)}
                      />
                      <TagChip color={t.color ?? undefined}>{t.name}</TagChip>
                    </label>
                  </li>
                ))}
              </ul>
            ) : null}
            {canCreate ? (
              <button
                type="button"
                disabled={create.isPending}
                onClick={() => create.mutate(q)}
                className="flex h-8 items-center gap-2 rounded px-2 text-left text-sm text-text-2 hover:bg-surface-2 hover:text-text-1 disabled:opacity-60"
              >
                <Plus className="size-3.5" /> Criar tag{' '}
                <TagChip color={tagColorFor(q)}>{q}</TagChip>
              </button>
            ) : null}
            {shown.length === 0 && !canCreate ? (
              <p className="px-2 py-3 text-sm text-text-3">
                {tags.length === 0
                  ? canManage
                    ? 'Nenhuma tag ainda. Digite um nome para criar.'
                    : 'Nenhuma tag cadastrada. Peça a quem gerencia tags.'
                  : 'Nenhuma tag com esse nome.'}
              </p>
            ) : null}
            {error ? <p className="px-2 text-xs text-danger">{error}</p> : null}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

/** Contexto (cliente / projeto / departamento / interno) como select nativo. */
export function ContextSelect({
  value,
  onChange,
  id,
  disabled,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  id?: string;
  disabled?: boolean;
}) {
  const { data: contexts = [] } = useContexts({ status: 'ACTIVE' });
  const groups = {
    CLIENT: 'Clientes',
    PROJECT: 'Projetos',
    DEPARTMENT: 'Departamentos',
    INTERNAL: 'Interno',
  } as const;
  return (
    <Select
      id={id}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
    >
      <option value="">Sem contexto</option>
      {(Object.keys(groups) as (keyof typeof groups)[]).map((type) => {
        const items = contexts.filter((c) => c.type === type);
        if (items.length === 0) return null;
        return (
          <optgroup key={type} label={groups[type]}>
            {items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </optgroup>
        );
      })}
    </Select>
  );
}
